import type { NavItem } from '@core/interfaces';
import { getHomePath } from '@core/utils/routes';
import type { ResolvedRoute } from '@core/utils/routes';
import { toProperCase } from '@core/utils/string';

export interface BreadcrumbItem {
  label: string;
  path: string;
  active: boolean;
}

/**
 * Builds breadcrumb items from pathname, nav items, and the resolved route.
 * Resolved route should come from getResolvedRoute(pathname, navItems) in routes.ts.
 */
export function buildBreadcrumbs(
  pathname: string,
  navItems: NavItem[],
  resolvedRoute: ResolvedRoute | undefined
): BreadcrumbItem[] {
  const homePath = getHomePath(navItems);
  const result: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      path: homePath,
      active: pathname === homePath,
    },
  ];
  if (pathname === homePath) return result;

  const push = (label: string, path: string, active: boolean) => {
    result.push({ label, path, active });
  };

  if (resolvedRoute?.parent && resolvedRoute.childTitle) {
    push(resolvedRoute.parent.title, resolvedRoute.parent.path, false);
    push(resolvedRoute.childTitle, resolvedRoute.path, true);
    return result;
  }

  if (resolvedRoute) {
    const navItem = navItems.find((item) => item.path === resolvedRoute.path);
    if (navItem) {
      const routeBase = resolvedRoute.path.endsWith('/*')
        ? resolvedRoute.path.slice(0, -2)
        : resolvedRoute.path;
      push(navItem.title, routeBase, pathname === routeBase);
      if (pathname !== routeBase) {
        const suffix = pathname.replace(`${routeBase}/`, '');
        if (suffix) {
          const title = suffix
            .split('-')
            .map((word) => toProperCase(word))
            .join(' ');
          push(title, pathname, true);
        }
      }
    }
  }

  return result;
}
